import { Icon } from '@iconify/react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const PRIMARY_COLOR = '#233F93';
const ACTIVE_BG = '#e4e8f7';

const Navbar = () => {
  const { pathname } = useLocation();
  const [openNav, setOpenNav] = useState(false);
  const [openDrop, setOpenDrop] = useState(false);
  const dropdownCatalogosRef = useRef(null);

  const links = [
    //{ label: 'Home', path: '/dashboard', icon: 'mdi:home-outline' },
    { label: 'Carga DME', path: '/carga-dme', icon: 'mdi:upload-box-outline' },
    { label: 'Log Cargas', path: '/log-cargas', icon: 'mdi:blog' },
    { label: 'Configuración cadena', path: '/config-dme', icon: 'mdi:account-cog' }, 
    //{ label: 'Archivos', path: '/archivos', icon: 'mdi:folder-settings-outline' },
    //{ label: 'Log', path: '/log', icon: 'mdi:file-chart-outline' },
  ];

  const catalogos = [
    { label: 'Usuarios', path: '/usuarios', icon: 'mdi:account-outline' },
    { label: 'Roles', path: '/lista-roles', icon: 'mdi:account-key-outline' },
    { label: 'Permisos', path: '/lista-permisos', icon: 'mdi:shield-check-outline' },
  ];

  const isCatalogosActive = () =>
    catalogos.some((item) => pathname.startsWith(item.path));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownCatalogosRef.current &&
        !dropdownCatalogosRef.current.contains(event.target)
      ) {
        setOpenDrop(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMobileLinkClick = () => {
    setOpenNav(false);
    setOpenDrop(false);
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md shadow border-t border-gray-200 sticky top-0 z-10">
      <div className="px-4 py-2 flex items-center justify-between md:justify-start md:space-x-6 text-sm font-medium">
        <button
          onClick={() => setOpenNav(!openNav)}
          className="md:hidden text-gray-700 focus:outline-none cursor-pointer"
          aria-label="Abrir o cerrar menú de navegación"
          aria-controls="main-navigation"
          aria-expanded={openNav}
          type="button"
        >
          {openNav ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        <div className="hidden md:flex space-x-2" id="main-navigation" role="menubar">
          {links.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `group flex items-center space-x-2 px-3 py-2 rounded-md transition-all cursor-pointer 
                ${
                  isActive
                    ? 'font-semibold'
                    : 'text-gray-700 hover:text-[#233F93] hover:bg-[#233F93]/20 hover:shadow-sm hover:shadow-blue-200/20'
                }`
              }
              style={({ isActive }) =>
                isActive ? { backgroundColor: ACTIVE_BG, color: PRIMARY_COLOR } : {}
              }
              onClick={() => setOpenDrop(false)}
              role="menuitem"
              aria-label={item.label}
            >
              <Icon icon={item.icon} className="w-4 h-4 transition-transform duration-150 group-hover:scale-110" />
              <span className="relative group">
                <span className="z-10">{item.label}</span>
                <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#233F93] group-hover:w-full transition-all duration-300"></span>
              </span>
            </NavLink>
          ))}

          <div ref={dropdownCatalogosRef} className="relative">
            <button
              onClick={() => setOpenDrop(!openDrop)}
              className={`group flex items-center space-x-1 px-3 py-2 rounded-md focus:outline-none cursor-pointer select-none transition-all 
                ${
                  isCatalogosActive()
                    ? ''
                    : 'text-gray-700 hover:text-[#233F93] hover:bg-[#233F93]/20 hover:shadow-sm hover:shadow-blue-200/20'
                }`}
              style={
                isCatalogosActive()
                  ? { backgroundColor: ACTIVE_BG, color: PRIMARY_COLOR }
                  : {}
              }
              aria-haspopup="true"
              aria-expanded={openDrop}
              type="button"
            >
              <span className="relative group">
                <span className="z-10">Catálogos</span>
                <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#233F93] group-hover:w-full transition-all duration-300"></span>
              </span>
              <Icon
                icon="mdi:chevron-down"
                className={`w-4 h-4 ml-1 transition-transform duration-300 ${
                  openDrop ? 'rotate-180' : ''
                } ${isCatalogosActive() ? 'text-[#233F93]' : ''}`}
              />
            </button>

            <ul
              className={`absolute bg-white border rounded shadow-2xl mt-2 w-56 z-[2000] overflow-hidden transition-all duration-300 ease-in-out ${
                openDrop ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
              }`}
              style={{ minWidth: '14rem' }}
              role="menu"
              aria-label="Catálogos"
            >
              {catalogos.map((item) => (
                <li key={item.label} role="none">
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center space-x-2 px-4 py-2 text-sm cursor-pointer transition-colors ${
                        isActive
                          ? `text-${PRIMARY_COLOR} font-semibold bg-[${ACTIVE_BG}]`
                          : 'text-gray-700 hover:bg-gray-100'
                      }`
                    }
                    onClick={handleMobileLinkClick}
                    role="menuitem"
                    aria-label={item.label}
                  >
                    <Icon icon={item.icon} className="w-4 h-4" />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {openNav && (
        <div className="md:hidden px-4 pb-4 space-y-2 text-sm font-medium text-gray-700 bg-white/90 backdrop-blur-md border-t border-gray-200">
          {[...links, ...catalogos].map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center space-x-2 px-2 py-2 rounded cursor-pointer transition-colors ${
                  isActive
                    ? 'font-semibold'
                    : 'text-gray-700 hover:text-[#233F93] hover:bg-[#233F93]/20'
                }`
              }
              style={({ isActive }) =>
                isActive ? { backgroundColor: ACTIVE_BG, color: PRIMARY_COLOR } : {}
              }
              onClick={handleMobileLinkClick}
            >
              <Icon icon={item.icon} className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
